import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

import { config } from "../../config/config";
import { partTypeWheel } from "../../constants/partTypes";
import { useLanguage } from "../../i18n/useLanguage";
import { AccessoryPartItem } from "../../queries/useSearchAccessoriesByVehicle";
import { Button } from "../../ui/Button/Button";
import { Image } from "../../ui/Image/Image";
import { KeyValue } from "../../ui/KeyValue/KeyValue";
import { Placeholder } from "../../ui/Placeholder";
import { PriceDisplay } from "../../ui/PriceDisplay/PriceDisplay";
import { InfoIcon } from "../../ui/icons/InfoIcon";
import { getPartFallbackSrc } from "../../util/getPartFallbackSrc";
import { getPartName } from "../../util/getPartName";
import { CartButton } from "../Cart/CartButton/CartButton";
import { usePriceSelectState } from "../PriceSelect/usePriceSelectState";
import { usePricing } from "../PriceSelect/usePricing";
import { usePricingLabelOverride } from "../PriceSelect/usePricingLabelOverride";
import { getProductModalHash } from "../ProductDetails/ProductModal";
import { getAccessoryInstallationKey } from "./getAccessoryInstallationKey";

type Props = {
  part: AccessoryPartItem | null;
  vehicleId?: string;
};

export const AccessoryTile: FunctionComponent<Props> = ({
  part,
  vehicleId,
}) => {
  const [language] = useLanguage();
  const { t } = useTranslation();
  const [pricing, setPricing] = usePriceSelectState([]);
  const pricingLabelOverride = usePricingLabelOverride(part);

  usePricing({
    defaultQuantity: "1",
    isStaggered: false,
    onReady: setPricing,
    part,
  });

  const [primaryPricing] = pricing;

  const installationKey = getAccessoryInstallationKey(
    part?.partDetails.primaryPart.accessoryRequiresInstallation
  );

  const detailsLink = getProductModalHash(part?.partDetails.primaryPart.itemId);

  return (
    <figure className="border border-edge rounded-panel flex flex-col overflow-hidden transition-colors bg-paper">
      <div className="grid grid-cols-5 gap-4 md:gap-6 flex-1">
        <Image
          alt="product image"
          className="self-center col-span-2 aspect-square object-cover h-full"
          errorClassName="opacity-20 blur-[8px]"
          fallbackSrc={getPartFallbackSrc(partTypeWheel)}
          pulsate={!part}
          src={part?.partDetails.primaryPart.accessoryImageLink}
        />
        <div className="col-span-3 flex flex-col space-y-3 justify-center">
          <h2 className="text-lg leading-4 lg:leading-5 lg:text-2xl md:font-medium">
            <Placeholder className="w-24" loading={!part}>
              {getPartName(part, language)}
            </Placeholder>
          </h2>
          <div>
            {config.brandName === "Mazda" && (
              <KeyValue className="text-sm">
                <>{t("searchResults.partNumber")}:</>
                <Placeholder className="w-20" loading={!part}>
                  {part?.partDetails.primaryPart.accessoryPartNumber}
                </Placeholder>
              </KeyValue>
            )}
            <KeyValue className="text-sm">
              <>{t("searchResults.installationRequired")}:</>
              <Placeholder className="w-[2em]" loading={!part}>
                {t(`searchResults.${installationKey}`)}
              </Placeholder>
            </KeyValue>
          </div>
          <KeyValue className="text-lg md:text-xl">
            <Placeholder className="w-[3em]" loading={!part}>
              {pricingLabelOverride || t("cart.price")}:
            </Placeholder>
            <Placeholder className="w-[3.6em]" loading={!part}>
              <PriceDisplay>
                {primaryPricing?.pricingOption.context.displayPrice}
              </PriceDisplay>
            </Placeholder>
          </KeyValue>
        </div>
      </div>
      <nav className="tw-weel-accessory__actions flex items-center border-t border-inherit transition-colors">
        <Link to={detailsLink}>
          <Button
            aria-label="viewDetails"
            className="rounded-none h-14 flex-1 font-light md:text-base text-right justify-end"
            disabled={!part}
            element="a"
          >
            <InfoIcon />
            {t("searchResults.viewDetails")}
          </Button>
        </Link>
        <div className="border-r border-inherit h-5" />
        <CartButton
          className="rounded-none h-14 flex-1  font-light md:text-base text-left justify-start"
          createPackageClassName={null}
          isStaggered={false}
          part={part}
          pricing={pricing}
          vehicleId={Number(vehicleId) || null}
          vehicleInfo={null}
        />
      </nav>
    </figure>
  );
};
